import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useRevalidator, useRouteLoaderData } from 'react-router-dom';
import { apiAdmin } from '@/api/admin';
import { ApiError } from '@/api/errors';
import type { BrandDto } from '@/api/types';
import { getBrandsByProduct, invalidateBrandsForProduct } from '@/api/lookupCache';
import { useCopy } from '@/hooks/useCopy';
import { useSnackbar } from '@/components/Snackbar';
import type { TenantWorkspaceLoaderData } from './TenantWorkspace';

export function TenantBranding() {
  const data = useRouteLoaderData('tenant-workspace') as TenantWorkspaceLoaderData;
  const tenant = data.tenant;
  const brand = data.brand;
  // SASADM-0135 — the tenant's product, resolved the same way every tab does (no product field on
  // the loader data itself). null when the tenant has no product; this component then shows every
  // warning/section exactly as it does today, unchanged.
  const product = tenant?.productId ? data.products.find((p) => p.id === tenant.productId) : null;
  const isSelfContained = (product?.appTopology ?? 'COMPOSED') === 'SELF_CONTAINED';
  const { t } = useCopy();
  const snack = useSnackbar();
  const revalidator = useRevalidator();
  const [pickerOpen, setPickerOpen] = useState(false);

  async function handleClear() {
    try {
      await apiAdmin.tenants.setBrand(tenant.id, null);
      snack.success(t('snackbar.brandCleared', 'Brand cleared.'));
      revalidator.revalidate();
    } catch (err) {
      if (err instanceof ApiError) snack.error(t(`errors.codes.${err.code}`, err.code));
    }
  }

  if (!tenant.productId) {
    return (
      <Alert severity="error">
        {t('branding.noProduct', 'Assign a product before configuring branding.')}
      </Alert>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Card variant="outlined">
          <CardContent>
            <Typography
              variant="overline"
              sx={{
                color: 'text.secondary',
              }}
            >
              {t('branding.current', 'Current brand')}
            </Typography>
            {brand ? (
              <Box>
                <Typography variant="h3">{brand.displayName}</Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {brand.slug}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                  {brand.authProviders.map((p) => (
                    <Chip key={p} label={p} size="small" />
                  ))}
                </Stack>
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                  {colorSwatch(brand.colors.primary)}
                  {colorSwatch(brand.colors.background)}
                  {colorSwatch(brand.colors.text)}
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    display: 'block',
                    mt: 1,
                  }}
                >
                  Signup {brand.signupEnabled ? 'enabled' : 'disabled'} · Password reset{' '}
                  {brand.passwordResetEnabled ? 'enabled' : 'disabled'}
                </Typography>
              </Box>
            ) : (
              <Typography sx={{ mt: 1 }}>
                {t('branding.inherits', 'Inherits product default')}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <Card variant="outlined">
          <CardContent>
            <Typography
              variant="overline"
              sx={{
                color: 'text.secondary',
              }}
            >
              {t('common.actions', 'Actions')}
            </Typography>
            {isSelfContained ? (
              <Alert severity="info" sx={{ mt: 1 }}>
                {t(
                  'branding.selfContainedNotApplicable',
                  'Branding does not apply to a self-contained product — it serves its own UI and assets.',
                )}
              </Alert>
            ) : (
              <Stack spacing={1} sx={{ mt: 1 }}>
                <Button variant="contained" onClick={() => setPickerOpen(true)}>
                  {t('branding.assignDifferent', 'Assign a different brand')}
                </Button>
                {brand ? (
                  <>
                    <Button variant="outlined" onClick={() => void handleClear()}>
                      {t('branding.clear', 'Clear brand (inherit default)')}
                    </Button>
                    <Button component={RouterLink} to={`/brands/${brand.id}`}>
                      {t('branding.editThis', 'Edit this brand')}
                    </Button>
                  </>
                ) : null}
              </Stack>
            )}
          </CardContent>
        </Card>
      </Grid>
      <BrandPicker
        open={pickerOpen}
        tenantId={tenant.id}
        productId={tenant.productId}
        onClose={() => setPickerOpen(false)}
        onPicked={() => {
          setPickerOpen(false);
          snack.success(t('snackbar.brandAssigned', 'Brand assigned.'));
          revalidator.revalidate();
        }}
      />
    </Grid>
  );
}

function colorSwatch(color: unknown) {
  if (typeof color !== 'string') return null;
  return (
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        bgcolor: color,
        border: '1px solid rgba(0,0,0,0.12)',
      }}
      title={color}
    />
  );
}

interface BrandPickerProps {
  open: boolean;
  tenantId: number;
  productId: number;
  onClose: () => void;
  onPicked: () => void;
}

function BrandPicker({ open, tenantId, productId, onClose, onPicked }: BrandPickerProps) {
  const { t } = useCopy();
  const snack = useSnackbar();
  const [brands, setBrands] = useState<BrandDto[] | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    // Move the synchronous `setBrands(null)` loading-reset inside the async path
    // so the effect body holds no synchronous setState. Behavior is identical:
    // the picker shows the loading state, then the fetched (or empty) list.
    void (async () => {
      setBrands(null);
      try {
        const rows = await getBrandsByProduct(productId);
        if (!cancelled) setBrands(rows);
      } catch {
        if (!cancelled) setBrands([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, productId]);

  async function pick(brandId: number | null) {
    setBusy(true);
    try {
      await apiAdmin.tenants.setBrand(tenantId, brandId);
      invalidateBrandsForProduct(productId);
      onPicked();
    } catch (err) {
      if (err instanceof ApiError) snack.error(t(`errors.codes.${err.code}`, err.code));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('branding.pickerTitle', 'Pick a brand')}</DialogTitle>
      <DialogContent>
        <List>
          <ListItemButton onClick={() => void pick(null)} disabled={busy}>
            <ListItemText
              primary={t('branding.inherits', 'Inherits product default')}
              secondary="brandId = null"
            />
          </ListItemButton>
          {brands === null ? null : brands.length === 0 ? (
            <Alert severity="info" sx={{ mt: 1 }}>
              {t('branding.pickerEmpty', '')}
            </Alert>
          ) : (
            brands.map((b) => (
              <ListItemButton key={b.id} onClick={() => void pick(b.id)} disabled={busy}>
                <ListItemText primary={b.displayName} secondary={b.slug} />
              </ListItemButton>
            ))
          )}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t('common.cancel', 'Cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
